import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, ShieldCheck } from "lucide-react";
import { SERVICES } from "@/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", phone: "", email: "", address: "", city: "", zip: "", service: SERVICES[0].title, message: "", discount_eligible: false };

export default function ContactForm({ defaultService }) {
  const [form, setForm] = useState({ ...EMPTY, service: defaultService || EMPTY.service });
  const [website, setWebsite] = useState(""); // honeypot — hidden from real users
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const startRef = useRef(Date.now());

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    // Honeypot + timing: silently accept bots without sending anything
    if (website || Date.now() - startRef.current < 2000) {
      setDone(true);
      return;
    }
    if (!form.name || !form.phone || !form.email) { toast.error("Please fill in your name, phone and email."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/service-requests`, { ...form, website });
      setDone(true);
      toast.success("Request sent! Juli's team will reach out shortly.");
      setForm({ ...EMPTY, service: defaultService || EMPTY.service });
    } catch (err) {
      if (err.response?.status === 429) toast.error("Too many requests. Please try again in a few minutes or call us at (239) 671-4867.");
      else if (err.response?.status === 400) toast.error("Please enter a valid name and phone number.");
      else toast.error("Something went wrong. Please call us at (239) 671-4867.");
    } finally { setLoading(false); }
  };

  const field = "w-full rounded-xl border border-sand bg-bone px-4 py-3 text-charcoal placeholder-ink/40 outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/30 transition-all duration-200";

  if (done) {
    return (
      <div data-testid="contact-success" className="rounded-2xl bg-sage p-10 text-center text-bone">
        <h3 className="font-heading text-3xl mb-3">Thank you!</h3>
        <p className="text-bone/80">Your request is in. We'll be in touch soon — or call us anytime at (239) 671-4867.</p>
        <button data-testid="contact-reset-btn" onClick={() => setDone(false)} className="mt-6 rounded-full bg-terracotta px-6 py-2.5 font-semibold hover:bg-terracotta-dark transition-colors duration-200">Send another</button>
      </div>
    );
  }

  return (
    <form data-testid="contact-form" onSubmit={submit} className="relative rounded-2xl bg-white border border-sand p-6 md:p-8 shadow-sm space-y-4">
      <div aria-hidden="true" className="absolute left-[-9999px] top-auto h-0 w-0 overflow-hidden" tabIndex={-1}>
        <label>Website<input type="text" name="website" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} /></label>
      </div>
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Customer information</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <input data-testid="contact-name" className={field} placeholder="Your name" value={form.name} onChange={set("name")} />
        <input data-testid="contact-phone" className={field} placeholder="Phone number" value={form.phone} onChange={set("phone")} />
      </div>
      <input data-testid="contact-email" type="email" className={field} placeholder="Email address" value={form.email} onChange={set("email")} />

      <div className="pt-2">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Service address</p>
        <div className="mt-3 space-y-4">
          <input data-testid="contact-address" className={field} placeholder="Street address" value={form.address} onChange={set("address")} />
          <div className="grid sm:grid-cols-2 gap-4">
            <input data-testid="contact-city" className={field} placeholder="City" value={form.city} onChange={set("city")} />
            <input data-testid="contact-zip" className={field} placeholder="Zip code" value={form.zip} onChange={set("zip")} />
          </div>
        </div>
      </div>

      <select data-testid="contact-service" className={field} value={form.service} onChange={set("service")}>
        {SERVICES.map((s) => <option key={s.slug} value={s.title}>{s.title}</option>)}
        <option value="Other / Not sure">Other / Not sure</option>
      </select>
      <textarea data-testid="contact-message" rows={4} className={field} placeholder="Tell us what's going on with your A/C..." value={form.message} onChange={set("message")} />
      <label data-testid="contact-discount-label" className={`flex items-start gap-3 rounded-xl border p-4 cursor-pointer transition-colors duration-200 ${form.discount_eligible ? "border-terracotta bg-terracotta/5" : "border-sand bg-bone hover:border-terracotta/50"}`}>
        <input data-testid="contact-discount-checkbox" type="checkbox" checked={form.discount_eligible}
          onChange={(e) => setForm({ ...form, discount_eligible: e.target.checked })}
          className="mt-0.5 h-5 w-5 shrink-0 rounded border-sand accent-[#C0583F] cursor-pointer" />
        <span className="text-sm text-charcoal">
          <span className="flex items-center gap-1.5 font-semibold"><ShieldCheck size={15} className="text-terracotta" /> I'm a veteran or first responder</span>
          <span className="block text-ink/70 mt-0.5">Thank you for your service — you'll receive 10% off. Valid ID may be requested.</span>
        </span>
      </label>
      <button data-testid="contact-submit-button" type="submit" disabled={loading}
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-terracotta px-6 py-4 font-semibold text-bone hover:bg-terracotta-dark disabled:opacity-60 transition-colors duration-200">
        {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
        {loading ? "Sending..." : "Request Service"}
      </button>
    </form>
  );
}
